import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AutoComplete } from 'primeng/autocomplete';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Invoice } from '../../../../modals/invoice.model';
import {
  InvoiceSearchServiceService,
  SalesReturnListResult,
} from '../../../shared/services/invoice-search.service.service';
import {
  DEFAULT_INVOICE_SELECTION_CONFIG,
  InvoiceSelectionConfig,
  InvoiceSelectionEvent,
  InvoiceSuggestion,
} from '../../models/invoice-selection.model';

// PrimeNG imports
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { IndustryService } from '../../../../services/industry.service';
import { IndustryUiConfig } from '../../industry-ui.model';
@Component({
  selector: 'app-invoice-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    AutoCompleteModule,
    ToggleButtonModule,
    SkeletonModule,
    RadioButtonModule,
  ],
  providers: [InvoiceSearchServiceService],
  templateUrl: './invoice-details-dialog.component.html',
  styleUrl: './invoice-details-dialog.component.scss',
})
export class InvoiceDetailsDialogComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  // ========== INPUTS ==========

  @Input() visible: boolean = false;
  @Input() config: Partial<InvoiceSelectionConfig> = {};
  @Input() enableCursorFocus: boolean = true;

  // ========== OUTPUTS ==========

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() invoiceSelected = new EventEmitter<InvoiceSelectionEvent>();
  @Output() dialogClosed = new EventEmitter<void>();

  // ========== VIEW CHILDREN ==========

  @ViewChild('invoiceSearchInput') invoiceSearchInput!: AutoComplete;
  /** Reference to the outer scroll container div in the template */
  @ViewChild('tableScrollContainer')
  tableScrollContainerRef!: ElementRef<HTMLDivElement>;

  // ========== COMPONENT STATE ==========

  // Configuration
  mergedConfig: InvoiceSelectionConfig = DEFAULT_INVOICE_SELECTION_CONFIG;

  // Search state
  searchControl = new FormControl('');
  useFullSearch: boolean = true;
  currentSearchTerm: string = '';
  isLoadingInvoices: boolean = false;

  // Data
  dialogInvoiceList: Invoice[] = [];
  filteredInvoiceSuggestions: InvoiceSuggestion[] = [];
  selectedInvoiceForDialog: any = null;

  totalCount: any = 0;
  // ──── Pagination state ────────────────────────────────────────────────────
  /** Current page number (1-indexed) */
  currentPage: number = 1;
  /** Number of records to fetch per page */
  readonly pageSize: number = 20;
  /** True while a load-more request is in flight */
  isLoadingMore: boolean = false;
  /** Becomes false once the API returns fewer records than pageSize */
  hasMoreData: boolean = true;
  // ─────────────────────────────────────────────────────────────────────────

  // Keyboard navigation
  private currentInvoiceRowIndex: number = 0;
  private invoiceTableKeyHandler: ((event: KeyboardEvent) => void) | null =
    null;

  // Lifecycle
  private destroy$ = new Subject<void>();
  // Loading state
  loadingTable = false;
  // Whether we are currently fetching full invoice details (after row click)
  isLoadingDetails: boolean = false;

  // Scroll listener cleanup reference
  private scrollListener: (() => void) | null = null;
  industryConfig!: IndustryUiConfig;
  // ========== CONSTRUCTOR ==========

  constructor(
    private invoiceSearchService: InvoiceSearchServiceService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private industryService: IndustryService,
  ) {}

  // ========== LIFECYCLE HOOKS ==========

  ngOnInit(): void {
    // Merge config with defaults
    this.mergedConfig = { ...DEFAULT_INVOICE_SELECTION_CONFIG, ...this.config };

    // Set initial search mode
    this.useFullSearch = this.mergedConfig.defaultSearchMode === 'full';

    // Setup search handler for Full Search mode
    this.setupFullSearchSubscription();
  }

  get showLoyaltyColumn(): boolean {
    return !this.industryService.isMobile();
  }

  ngAfterViewInit(): void {
    // Scroll listener will be attached after the dialog opens (p-table is not
    // rendered until visible === true).
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupInvoiceTableNavigation();
    this.detachScrollListener();
  }

  // ========== PUBLIC API ==========

  /**
   * Open dialog programmatically
   */
  openDialog(): void {
    this.visible = true;
    this.visibleChange.emit(true);
    // Load initial data
    this.loadInitialData();

    // Focus search input
    if (this.enableCursorFocus && this.mergedConfig.autoFocus) {
      setTimeout(() => this.focusInvoiceSearch(), 100);
    }
  }

  /**
   * Close dialog programmatically
   */
  closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.dialogClosed.emit();
    this.resetDialogState();
  }

  onVisibleChange(isVisible: boolean) {
    this.visible = isVisible;
    this.visibleChange.emit(isVisible);
    if (!isVisible) {
      this.dialogClosed.emit();
    }
  }

  /**
   * Clear search and results
   */
  clearSearch(): void {
    this.searchControl.patchValue('');
    this.currentSearchTerm = '';
    this.filteredInvoiceSuggestions = [];
    this.dialogInvoiceList = [];
    this.selectedInvoiceForDialog = null;
  }

  // ========== DIALOG EVENTS ==========

  /**
   * Called when dialog is shown
   */
  onDialogShow(): void {
    this.loadInitialData();
    if (this.enableCursorFocus && this.mergedConfig.autoFocus) {
      setTimeout(() => this.focusInvoiceSearch(), 100);
    }
    // Attach scroll listener once the DOM is ready
    setTimeout(() => this.attachScrollListener(), 300);
  }

  /**
   * Called when dialog is hidden
   */
  onDialogHide(): void {
    this.resetDialogState();
    this.detachScrollListener();
  }

  /**
   * Handle close button click
   */
  onCloseClick(): void {
    this.closeDialog();
  }

  // ========== SEARCH MODE MANAGEMENT ==========

  /**
   * Toggle between Full Search and Quick Search
   */
  onSearchModeToggle(): void {
    const currentQuery = this.searchControl.value;

    if (!this.useFullSearch) {
      // Switched to Quick Search - load all invoices if needed
      const cachedInvoices = this.invoiceSearchService.getCachedInvoices();
      if (cachedInvoices.length === 0) {
        this.loadAllInvoices();
      } else {
        this.dialogInvoiceList = [...cachedInvoices];
      }
    }

    // Clear suggestions
    this.filteredInvoiceSuggestions = [];

    // Re-trigger search if there's a query
    if (
      currentQuery &&
      currentQuery.trim().length >= this.mergedConfig.minSearchLength!
    ) {
      this.handleInvoiceSearch(currentQuery);
    }
  }

  // ========== SEARCH OPERATIONS ==========

  /**
   * Setup Full Search subscription (debounced API search)
   */
  private setupFullSearchSubscription(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.mergedConfig.debounceTime!),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        if (this.useFullSearch) {
          this.handleFullSearch(value);
        } else {
          // Handle Quick Search filtering directly from the same input
          this.currentSearchTerm = value?.trim() || '';
          this.filterTableResults(this.currentSearchTerm);
        }
      });
  }

  private handleFullSearch(value: string | null): void {
    if (value && value.trim().length >= this.mergedConfig.minSearchLength!) {
      // Reset pagination for a fresh search
      this.resetPagination();
      this.isLoadingInvoices = true;
      this.currentSearchTerm = value.trim();

      // SALES RETURN FLOW: search via GET /v1/sale/salesreturnlist?search=<query>&page=1&pageSize=N
      this.invoiceSearchService
        .loadSalesReturnList(value.trim(), 1, this.pageSize)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: SalesReturnListResult) => {
            this.isLoadingInvoices = false;
            const items = res?.data ?? [];
            this.totalCount = res?.totalCount ?? items.length;
            this.dialogInvoiceList = items;
            this.hasMoreData = items.length >= this.pageSize;
            this.cdr.detectChanges();
          },
          error: (error) => {
            this.isLoadingInvoices = false;
            console.error('Sales return search error:', error);
            this.dialogInvoiceList = [];
            this.totalCount = 0;
            this.cdr.detectChanges();
          },
        });

      // [PREVIOUS FLOW — commented out, do not delete]
      // this.invoiceSearchService.searchInvoices(value.trim(), true)
      //   .pipe(takeUntil(this.destroy$))
      //   .subscribe({
      //     next: (invoices) => {
      //       this.isLoadingInvoices = false;
      //       this.dialogInvoiceList = invoices;
      //       console.log(this.dialogInvoiceList.length, this.dialogInvoiceList);
      //       this.cdr.detectChanges();
      //     },
      //     error: (error) => {
      //       this.isLoadingInvoices = false;
      //       console.error('Full search error:', error);
      //       this.dialogInvoiceList = [];
      //       this.cdr.detectChanges();
      //     }
      //   });
    } else {
      // Empty search term -> fallback to loading sales return list (page 1)
      this.currentSearchTerm = '';
      this.loadAllInvoices();
    }
  }

  /**
   * Handle Quick Search mode input (legacy handler, preserved for safety but mostly unused now)
   */
  onQuickSearchInput(event: any): void {
    const query = event.target.value;
    this.currentSearchTerm = query?.trim() || '';
    this.filterTableResults(query);
  }

  /**
   * Filter table results (client-side)
   */
  private filterTableResults(query: string | null): void {
    const cachedInvoices = this.invoiceSearchService.getCachedInvoices();
    this.dialogInvoiceList = this.invoiceSearchService.filterInvoicesClientSide(
      cachedInvoices,
      query,
    );
  }

  /**
   * Generic search handler
   */
  private handleInvoiceSearch(query: string | null): void {
    this.currentSearchTerm = query?.trim() || '';

    if (!query || query.trim().length === 0) {
      if (this.useFullSearch) {
        this.filteredInvoiceSuggestions = [];
      } else {
        this.dialogInvoiceList = [
          ...this.invoiceSearchService.getCachedInvoices(),
        ];
      }
    } else if (query.trim().length >= this.mergedConfig.minSearchLength!) {
      if (!this.useFullSearch) {
        this.filterTableResults(query.trim());
      }
    }
  }

  // ========== DATA LOADING ==========

  /**
   * Load initial data when dialog opens
   * SALES RETURN FLOW: calls GET /v1/sale/salesreturnlist?page=1&pageSize=N
   */
  private loadInitialData(): void {
    this.loadAllInvoices();
  }

  /**
   * Load page 1 of invoices (resets list).
   * SALES RETURN FLOW: GET /v1/sale/salesreturnlist?page=1&pageSize=N
   *
   * [PREVIOUS FLOW — kept for reference, do not delete]
   * Was: this.invoiceSearchService.loadAllInvoices() → GET /v1/sale (POST)
   */
  private loadAllInvoices(): void {
    this.resetPagination();
    this.isLoadingInvoices = true;

    // NEW: Sales Return List API with pagination
    this.invoiceSearchService
      .loadSalesReturnList(undefined, 1, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: SalesReturnListResult) => {
          this.isLoadingInvoices = false;
          const items = res?.data ?? [];
          this.totalCount = res?.totalCount ?? items.length;
          this.dialogInvoiceList = [...items];
          this.hasMoreData = items.length >= this.pageSize;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.isLoadingInvoices = false;
          console.error('Load sales return list error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load sales return list',
            life: 3000,
          });
          this.cdr.detectChanges();
        },
      });

    // [PREVIOUS FLOW — commented out, do not delete]
    // this.invoiceSearchService.loadAllInvoices()
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe({
    //     next: (invoices) => {
    //       this.isLoadingInvoices = false;
    //       this.dialogInvoiceList = [...invoices];
    //       console.log(this.dialogInvoiceList.length, this.dialogInvoiceList);
    //       this.cdr.detectChanges();
    //     },
    //     error: (error) => {
    //       this.isLoadingInvoices = false;
    //       console.error('Load all invoices error:', error);
    //       this.messageService.add({
    //         severity: 'error',
    //         summary: 'Error',
    //         detail: 'Failed to load invoices',
    //         life: 3000
    //       });
    //       this.cdr.detectChanges();
    //     }
    //   });
  }

  // ========== INFINITE SCROLL — LOAD MORE ==========

  /**
   * Called when the user scrolls near the bottom of the table container.
   * Fetches the next page and appends results to the list.
   */
  loadMoreInvoices(): void {
    if (this.isLoadingMore || !this.hasMoreData || this.isLoadingInvoices) {
      return;
    }

    this.currentPage++;
    this.isLoadingMore = true;
    this.cdr.detectChanges();

    const searchQuery = this.currentSearchTerm || undefined;

    this.invoiceSearchService
      .loadSalesReturnList(searchQuery, this.currentPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: SalesReturnListResult) => {
          this.isLoadingMore = false;
          const items = res?.data ?? [];
          if (res?.totalCount !== undefined) {
            this.totalCount = res.totalCount;
          }
          if (items.length > 0) {
            this.dialogInvoiceList = [...this.dialogInvoiceList, ...items];
          }
          // If the API returned fewer records than the page size, we've reached the end
          this.hasMoreData = items.length >= this.pageSize;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.isLoadingMore = false;
          // Roll back page increment so user can retry by scrolling again
          this.currentPage--;
          console.error('Load more invoices error:', error);
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Attach a scroll event listener to the p-table's inner scrollable wrapper.
   * Triggers loadMoreInvoices() when the user scrolls within 80px of the bottom.
   */
  private attachScrollListener(): void {
    this.detachScrollListener(); // Clean up any previous listener

    // p-table with [scrollable]="true" creates a .p-datatable-wrapper div
    const container = document.querySelector(
      '.dense-invoice-dialog .p-datatable-wrapper',
    ) as HTMLElement | null;

    if (!container) {
      return;
    }

    this.scrollListener = () => {
      // Run outside Angular to avoid triggering change detection on every scroll event
      this.ngZone.runOutsideAngular(() => {
        const threshold = 80; // px from bottom
        const nearBottom =
          container.scrollTop + container.clientHeight >=
          container.scrollHeight - threshold;

        if (nearBottom) {
          this.ngZone.run(() => this.loadMoreInvoices());
        }
      });
    };

    container.addEventListener('scroll', this.scrollListener, {
      passive: true,
    });
  }

  /**
   * Remove the scroll event listener when dialog closes or component is destroyed.
   */
  private detachScrollListener(): void {
    if (!this.scrollListener) return;

    const container = document.querySelector(
      '.dense-invoice-dialog .p-datatable-wrapper',
    ) as HTMLElement | null;

    if (container) {
      container.removeEventListener('scroll', this.scrollListener);
    }
    this.scrollListener = null;
  }

  /**
   * Reset pagination back to page 1 and clear the list.
   * Called before any fresh load (initial, search change).
   */
  private resetPagination(): void {
    this.currentPage = 1;
    this.hasMoreData = true;
    this.dialogInvoiceList = [];
    this.totalCount = 0;
  }

  // ========== SELECTION HANDLERS ==========

  /**
   * Handle autocomplete selection (Full Search)
   */
  handleInvoiceSelect(selectedInvoice: any): void {
    const selected_Invoice = selectedInvoice.value;
    this.dialogInvoiceList = [selected_Invoice];
    this.selectedInvoiceForDialog = selected_Invoice;
  }

  /**
   * Handle row click selection.
   *
   * SALES RETURN FLOW:
   *   1. Validate the invoice is eligible for return.
   *   2. Fetch full details via GET /v1/sale/salesreturndetails/<uuid>.
   *   3. Emit the full invoice (with salesitems[]) to the parent so it can bind into the cart.
   *
   * [PREVIOUS FLOW — kept as comments below, do not delete]
   */
  onInvoiceRowClick(invoiceData: any): void {
    // Validate invoice eligibility
    const validation =
      this.invoiceSearchService.validateInvoiceForReturn(invoiceData);

    if (!validation.isValid) {
      this.closeDialog();
      this.messageService.add({
        severity: 'warn',
        summary: 'Return Not Allowed',
        detail: validation.reason,
        life: 3000,
      });
      return;
    }

    // Mark as selected in the UI while we fetch details
    this.selectedInvoiceForDialog = invoiceData;
    this.isLoadingDetails = true;
    const uuid: string = invoiceData?.invoice?.uuid;

    if (!uuid) {
      // Fallback: emit list-level data if uuid is missing
      this.isLoadingDetails = false;
      const event: InvoiceSelectionEvent = {
        invoice: invoiceData,
        selectionMethod: 'row-click',
        timestamp: new Date(),
      };
      this.invoiceSelected.emit(event);
      this.closeDialog();
      return;
    }

    // Fetch full sales return details (includes salesitems[] for cart binding)
    this.invoiceSearchService
      .getSalesReturnDetails(uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fullInvoiceData: any) => {
          this.isLoadingDetails = false;
          if (fullInvoiceData) {
            const event: InvoiceSelectionEvent = {
              invoice: fullInvoiceData, // full data with salesitems[] to bind into cart
              selectionMethod: 'row-click',
              timestamp: new Date(),
            };
            this.invoiceSelected.emit(event);
          } else {
            // Details fetch returned nothing — emit what we already have from list
            console.warn(
              'Sales return details returned null, falling back to list data',
            );
            const event: InvoiceSelectionEvent = {
              invoice: invoiceData,
              selectionMethod: 'row-click',
              timestamp: new Date(),
            };
            this.invoiceSelected.emit(event);
          }
          this.closeDialog();
        },
        error: (error: any) => {
          this.isLoadingDetails = false;
          console.error('Failed to fetch sales return details:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load invoice details. Please try again.',
            life: 3000,
          });
        },
      });

    // [PREVIOUS FLOW — commented out, do not delete]
    // // Update selection
    // this.selectedInvoiceForDialog = invoiceData;
    // // Emit selection event
    // const event: InvoiceSelectionEvent = {
    //   invoice: invoiceData,
    //   selectionMethod: 'row-click',
    //   timestamp: new Date()
    // };
    // this.invoiceSelected.emit(event);
    // this.closeDialog();
  }

  // ========== KEYBOARD NAVIGATION ==========

  /**
   * Handle autocomplete keydown (Tab to table navigation)
   */
  handleAutocompleteKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') return;

    if (event.key === 'Tab' && this.dialogInvoiceList.length > 0) {
      event.preventDefault();
      setTimeout(() => this.setupInvoiceTableNavigation(), 100);
    }
  }

  /**
   * Setup keyboard navigation for table
   */
  private setupInvoiceTableNavigation(): void {
    const tableRows = document.querySelectorAll(
      '.invoice-table-scrollable tbody tr',
    ) as NodeListOf<HTMLElement>;

    if (tableRows.length > 0) {
      this.currentInvoiceRowIndex = 0;
      this.highlightInvoiceRow(0);
      this.addInvoiceTableKeyboardListeners();
    }
  }

  /**
   * Add keyboard event listeners for table
   */
  private addInvoiceTableKeyboardListeners(): void {
    // Remove previous listener if exists
    if (this.invoiceTableKeyHandler) {
      document.removeEventListener('keydown', this.invoiceTableKeyHandler);
    }

    // Create new handler
    this.invoiceTableKeyHandler = (event: KeyboardEvent) => {
      const tableRows = document.querySelectorAll(
        '.invoice-table-scrollable tbody tr',
      ) as NodeListOf<HTMLElement>;

      const activeElement = document.activeElement as HTMLElement;
      if (
        !activeElement ||
        !activeElement.closest('.invoice-table-scrollable tbody tr')
      ) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (this.currentInvoiceRowIndex < tableRows.length - 1) {
            this.currentInvoiceRowIndex++;
            this.highlightInvoiceRow(this.currentInvoiceRowIndex);
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (this.currentInvoiceRowIndex > 0) {
            this.currentInvoiceRowIndex--;
            this.highlightInvoiceRow(this.currentInvoiceRowIndex);
          }
          break;

        case 'Enter':
          event.preventDefault();
          if (this.dialogInvoiceList[this.currentInvoiceRowIndex]) {
            this.onInvoiceRowClick(
              this.dialogInvoiceList[this.currentInvoiceRowIndex],
            );
            this.cleanupInvoiceTableNavigation();
          }
          break;

        case 'Escape':
          event.preventDefault();
          this.cleanupInvoiceTableNavigation();
          this.focusInvoiceSearch();
          break;
      }
    };

    document.addEventListener('keydown', this.invoiceTableKeyHandler);
  }

  /**
   * Highlight specific row
   */
  private highlightInvoiceRow(index: number): void {
    const tableRows = document.querySelectorAll(
      '.invoice-table-scrollable tbody tr',
    ) as NodeListOf<HTMLElement>;

    tableRows.forEach((row) => {
      row.style.outline = '';
      row.removeAttribute('tabindex');
    });

    if (tableRows[index]) {
      const currentRow = tableRows[index];
      currentRow.setAttribute('tabindex', '0');
      currentRow.style.outline = '2px solid #2A5EDE';
      currentRow.focus();
    }
  }

  /**
   * Cleanup keyboard navigation
   */
  private cleanupInvoiceTableNavigation(): void {
    if (this.invoiceTableKeyHandler) {
      document.removeEventListener('keydown', this.invoiceTableKeyHandler);
      this.invoiceTableKeyHandler = null;
    }

    const tableRows = document.querySelectorAll(
      '.invoice-table-scrollable tbody tr',
    ) as NodeListOf<HTMLElement>;

    tableRows.forEach((row) => {
      row.style.outline = '';
      row.removeAttribute('tabindex');
    });
  }

  // ========== UI UTILITIES ==========

  /**
   * Highlight search term in text
   */
  highlightSearchTerm(text: string): string {
    if (!this.currentSearchTerm || this.useFullSearch) return text;

    const regex = new RegExp(`(${this.currentSearchTerm})`, 'gi');
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  /**
   * Check if row should be highlighted
   */
  isRowHighlighted(invoiceData: any): boolean {
    if (!this.currentSearchTerm || this.useFullSearch) return false;

    const searchTerm = this.currentSearchTerm.toLowerCase();
    return (
      invoiceData.invoice.invoiceno
        .toString()
        .toLowerCase()
        .includes(searchTerm) ||
      invoiceData.invoice.user_id.toString().toLowerCase().includes(searchTerm)
    );
  }

  /**
   * Focus invoice search input
   */
  private focusInvoiceSearch(): void {
    const searchInput = document.querySelector(
      '.search-autocomplete input',
    ) as HTMLInputElement;

    if (searchInput) {
      searchInput.focus();
    }
  }

  /**
   * Reset dialog state
   */
  private resetDialogState(): void {
    this.searchControl.patchValue('');
    this.currentSearchTerm = '';
    this.filteredInvoiceSuggestions = [];
    this.dialogInvoiceList = [];
    this.selectedInvoiceForDialog = null;
    this.isLoadingInvoices = false;
    this.isLoadingMore = false;
    this.resetPagination();
    this.cleanupInvoiceTableNavigation();
  }
}
